import { DeleteOutlined, EditOutlined, PlusOutlined, SettingOutlined } from "@ant-design/icons";
import { Button, Col, Flex, Popconfirm, Row, Space, Table, Tooltip, message } from "antd";
import { useMemo, useState } from "react";
import styled from "styled-components";

import { formatCurrency, getCustomFieldsItems } from "common/utils";
import { TriggerModal } from "components/triggerModal";
import { UserDefinedFieldValues } from "components/userDefinedFieldValues";
import { TableAppearanceModal } from "features/appearance";
import { reconcileColumns } from "features/appearance/components/tableAppearanceModal";
import { useFetchDebtorAssetCategoriesQuery } from "features/debtorAssetCategories/debtorAssetCategoriesAPI";
import { useFetchAllDebtorAssetCustomFieldsQuery } from "features/userDefinedFields/debtorAssetCustomFieldsAPI";
import { useFetchDebtorAccountsQuery } from "features/debtorAccountsTable/agencyPortal/debtorAccountsAPI";
import {
  useDeleteDebtorAssetMutation,
  useFetchDebtorAssetsQuery,
} from "features/debtorAssets/debtorAssetsAPI";
import { PERMISSIONS } from "features/permissions";
import useAuthorizations from "features/permissions/hooks/useAuthorizations";
import { useAppearance } from "providers/appearanceProvider";
import AddDebtorAssetModal from "./addDebtorAssetModal";

const StyledTable = styled(Table)`
  cursor: pointer;
  width: 100%;

  & .ant-table-cell:not(th) {
    padding: 4px 8px !important;
  }
`;

const TABLE_ID = "debtorAssetsInfo";

export default function DebtorAssetsInfoTable({ debtorId }) {
  const [currentAsset, setCurrentAsset] = useState(null);
  const [isAssetModalVisible, setIsAssetModalVisible] = useState(false);

  const { data: assets, isLoading } = useFetchDebtorAssetsQuery({ debtorId });
  const { data: debtorAccounts } = useFetchDebtorAccountsQuery({ debtorId });
  const { data: categories } = useFetchDebtorAssetCategoriesQuery();
  const { data: customFields } = useFetchAllDebtorAssetCustomFieldsQuery();
  const [deleteAsset] = useDeleteDebtorAssetMutation();
  const { appearance } = useAppearance();

  const {
    data: {
      [PERMISSIONS.DEBTOR_ASSET__GET]: isGetAssetAuthorized,
      [PERMISSIONS.DEBTOR_ASSET__CREATE]: isCreateAssetAuthorized,
      [PERMISSIONS.DEBTOR_ASSET__UPDATE]: isUpdateAssetAuthorized,
      [PERMISSIONS.DEBTOR_ASSET__DELETE]: isDeleteAssetAuthorized,
    },
  } = useAuthorizations();

  // Build category map for display
  const categoryIdToNameMap = useMemo(() => {
    if (!categories) return {};
    return categories.reduce((acc, cat) => {
      acc[cat.id] = cat.name;
      return acc;
    }, {});
  }, [categories]);

  const accountIdToExternalIdMap = useMemo(() => {
    if (!debtorAccounts) return {};
    return debtorAccounts.reduce((acc, account) => {
      acc[account.id] = account.externalId;
      return acc;
    }, {});
  }, [debtorAccounts]);

  const addAsset = () => {
    setIsAssetModalVisible(true);
    setCurrentAsset(null);
  };

  const editAsset = (asset) => {
    setIsAssetModalVisible(true);
    setCurrentAsset(asset);
  };

  const onCancelAssetModal = () => {
    setIsAssetModalVisible(false);
    setCurrentAsset(null);
  };

  const onDoneAssetModal = () => {
    setIsAssetModalVisible(false);
    setCurrentAsset(null);
  };

  const handleDelete = async (asset) => {
    const result = await deleteAsset({
      debtorId,
      debtorAssetId: asset.id,
    });
    if ("data" in result) {
      message.success("Asset deleted successfully");
    }
  };

  const columns = [
    {
      key: "name",
      title: "Asset Name",
      dataIndex: "name",
      visible: true,
      sorter: (a, b) => (a.name || "").localeCompare(b.name || ""),
    },
    {
      key: "debtorAssetCategoryId",
      title: "Category",
      dataIndex: "debtorAssetCategoryId",
      render: (text) => categoryIdToNameMap[text] || "-",
      visible: true,
      sorter: (a, b) => {
        const nameA = categoryIdToNameMap[a.debtorAssetCategoryId] || "";
        const nameB = categoryIdToNameMap[b.debtorAssetCategoryId] || "";
        return nameA.localeCompare(nameB);
      },
    },
    {
      key: "value",
      title: "Value",
      dataIndex: "value",
      render: (text) => (text ? formatCurrency(text) : "-"),
      visible: true,
      sorter: (a, b) => (parseFloat(a.value) || 0) - (parseFloat(b.value) || 0),
    },
    {
      key: "accountIds",
      title: "Accounts",
      dataIndex: "accountIds",
      render: (text, record) => {
        const accountIds = record.accountIds || [];
        if (accountIds.length === 0) return "-";
        return accountIds.map((id) => accountIdToExternalIdMap[id] || id).join(", ");
      },
      visible: true,
    },
    {
      key: "customFieldValues",
      title: "User Defined Fields",
      dataIndex: "customFieldValues",
      render: (text, record) => {
        const customFieldItems = getCustomFieldsItems(record.customFieldValues, customFields);
        return <UserDefinedFieldValues customFieldItems={customFieldItems} />;
      },
      visible: true,
    },
    {
      title: "Actions",
      fixed: "right",
      width: 100,
      key: "actions",
      visible: true,
      render: (_, record) => {
        return (
          <Row wrap={false} align="middle">
            <Space size="middle">
              {isUpdateAssetAuthorized && (
                <Tooltip placement="bottom" title="Edit" key="edit">
                  <EditOutlined
                    key="edit"
                    onClick={(e) => {
                      e.stopPropagation();
                      editAsset(record);
                    }}
                  />
                </Tooltip>
              )}
              {isDeleteAssetAuthorized && (
                <Popconfirm
                  placement="topLeft"
                  okText="Yes"
                  title="Are you sure you want to delete this asset?"
                  onConfirm={async (e) => {
                    e.stopPropagation();
                    await handleDelete(record);
                  }}
                  onCancel={(e) => e.stopPropagation()}
                >
                  <DeleteOutlined key="delete" onClick={(e) => e.stopPropagation()} />
                </Popconfirm>
              )}
            </Space>
          </Row>
        );
      },
    },
  ];

  const reconciledColumns = reconcileColumns(columns, appearance?.items?.[TABLE_ID]).filter(
    (column) => column.visible,
  );

  return isGetAssetAuthorized ? (
    <>
      <Col>
        <Flex justify="space-between" align="middle">
          {isCreateAssetAuthorized && (
            <Button onClick={addAsset} icon={<PlusOutlined />} type="link">
              Add Asset
            </Button>
          )}
          <TriggerModal
            modal={TableAppearanceModal}
            tableId={TABLE_ID}
            defaultColumns={columns}
            paginationSelector={false}
          >
            <Tooltip title="Customize Appearance">
              <Button shape="circle" icon={<SettingOutlined />} type="text" />
            </Tooltip>
          </TriggerModal>
        </Flex>
        <StyledTable
          bordered
          scroll={{ x: "max-content" }}
          columns={reconciledColumns}
          dataSource={assets}
          loading={isLoading}
          rowKey="id"
        />
      </Col>
      {isAssetModalVisible && (
        <AddDebtorAssetModal
          debtorId={debtorId}
          asset={currentAsset}
          open={isAssetModalVisible}
          onCancel={onCancelAssetModal}
          onOk={onDoneAssetModal}
        />
      )}
    </>
  ) : null;
}
